import { RUNTIME, assertCloudConfigured } from '../../config/runtime';

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function parseResponse(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return null;
  try { return JSON.parse(text) as unknown; } catch { return text; }
}

export async function apiRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  assertCloudConfigured();
  const response = await fetch(`${RUNTIME.apiBaseUrl}${path.startsWith('/') ? path : `/${path}`}`, {
    credentials: 'include',
    ...init,
    headers: {
      Accept: 'application/json',
      ...(init.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
      ...init.headers,
    },
  });
  const payload = await parseResponse(response);
  if (!response.ok) {
    throw new ApiError(extractErrorMessage(payload, response.status), response.status, payload);
  }
  return payload as T;
}

// The Worker returns a canonical envelope `{ error: { code, message, requestId } }`.
// Older/legacy responses used `{ error: "message" }`. Support both.
function extractErrorMessage(payload: unknown, status: number): string {
  if (payload && typeof payload === 'object' && 'error' in payload) {
    const error = (payload as { error: unknown }).error;
    if (typeof error === 'string') return error;
    if (error && typeof error === 'object' && 'message' in error) {
      return String((error as { message: unknown }).message);
    }
  }
  return `Request failed (${status})`;
}
